import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import Link, { LinkProps } from '@mui/material/Link';
import Button, { ButtonProps } from '@mui/material/Button';

type ResendTimerProps = Omit<LinkProps | ButtonProps, 'onClick' | 'children'> & {
    seconds: number;
    onResend: () => void;
    resetOnLoad?: boolean;
    remainingSeconds?: number | null;
    disabled?: boolean;
    renderAs?: 'link' | 'button';
    children: ReactNode;
};

const normalizeSeconds = (value?: number | null) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return 0;
    return Math.max(0, Math.ceil(value));
};

export const ResendTimer = ({
    seconds,
    onResend,
    resetOnLoad = true,
    remainingSeconds,
    disabled = false,
    renderAs = 'link',
    children,
    sx,
    ...props
}: ResendTimerProps) => {
    const [timer, setTimer] = useState<number>(resetOnLoad ? seconds : 0);
    const prevRemainingRef = useRef<number | null | undefined>();

    useEffect(() => {
        if (typeof remainingSeconds === 'number') {
            const normalized = normalizeSeconds(remainingSeconds);
            if (prevRemainingRef.current !== normalized) {
                prevRemainingRef.current = normalized;
                setTimer(normalized);
            }
        } else if (typeof prevRemainingRef.current === 'number') {
            prevRemainingRef.current = undefined;
            setTimer(prev => (prev > 0 ? 0 : prev));
        }
    }, [remainingSeconds]);

    useEffect(() => {
        if (typeof remainingSeconds === 'number') return;
        if (timer <= 0) return;
        const intervalId = window.setInterval(() => {
            setTimer(prev => (prev <= 1 ? 0 : prev - 1));
        }, 1000);
        return () => window.clearInterval(intervalId);
    }, [timer, remainingSeconds]);

    const isCoolingDown = timer > 0;
    const isDisabled = disabled || isCoolingDown;

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        event.preventDefault();
        if (isDisabled) return;
        setTimer(seconds);
        onResend();
    };

    const label = useMemo(
        () => (isCoolingDown ? `Resend in ${timer}s` : children),
        [children, isCoolingDown, timer]
    );

    return renderAs === 'button' ? (
        <Button
            type="button"
            onClick={handleClick}
            disabled={isDisabled}
            aria-disabled={isDisabled}
            sx={sx}
            {...(props as ButtonProps)}
        >
            {label}
        </Button>
    ) : (
        <Link
            type="button"
            onClick={handleClick}
            aria-disabled={isDisabled}
            sx={{
                'color': '#202024',
                'cursor': isDisabled ? 'not-allowed' : 'pointer',
                'width': 'fit-content',
                'margin': '0 0 0 auto',
                'textAlign': 'right',
                'textDecoration': isDisabled ? 'none' : 'underline',
                'textDecorationColor': '#6E6E71',
                'pointerEvents': isDisabled ? 'none' : 'auto',
                '&:hover': {
                    textDecorationColor: '#202024',
                },
                ...sx,
            }}
            {...(props as LinkProps)}
        >
            {label}
        </Link>
    );
};

export type { ResendTimerProps };
